import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {Panel} from 'primeng/panel';
import {TableModule} from 'primeng/table';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {InputText} from 'primeng/inputtext';
import {Divider} from 'primeng/divider';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ConfirmationService} from 'primeng/api';
import {MessageService} from 'primeng/api';
import {DialogModule} from 'primeng/dialog';
import {ToastModule} from 'primeng/toast';
import { CommonEffects } from '../store/common.effects';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { selectBoundData, selectBoundTotal } from '../store/common.selector'
import { Bound } from '../../shared/models/common/bound.model';
import { selectIsLoadingAction } from '../../shared/store/global';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as action from '../store/common.actions';
import {toSignal} from '@angular/core/rxjs-interop';
import {BaseListComponent} from '../../shared/components/base-list.component';
import { AssetHistoryDialogComponent } from '../../shared/components/asset-history-dialog/asset-history-dialog.component';
import { ColumnFilters } from '../../shared/models/ui/table-column.model';

@Component({
  selector: 'bounds-list',
  templateUrl: './bounds-list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Panel,
    TableModule,
    Button,
    IconField,
    InputIcon,
    InputText,
    Divider,
    ConfirmDialogModule,
    DialogModule,
    ToastModule,
    AssetHistoryDialogComponent
  ],
  providers: [ConfirmationService, MessageService, CommonEffects],
  styleUrls: ['./bounds-list.component.scss']
})
export class BoundsListComponent extends BaseListComponent implements OnInit, OnDestroy {

  private messageService = inject(MessageService);
  private store$ = inject(Store<{ common: any }>)
  private actions$ = inject(Actions);

  bounds = toSignal(this.store$.select(selectBoundData), {initialValue: []});
  totalRecords = toSignal(this.store$.select(selectBoundTotal), {initialValue: 0});
  loading = toSignal(this.store$.select(selectIsLoadingAction('[Common] Find Bounds')), {initialValue: false});

  
  readonly auditResourceName = 'Bound';
  historyVisible = false;
  historyRecordId: number | string | null = null;
  historyRecordLabel = '';

  selectedBounds = signal<Bound[]>([]);

  // Add Bound Form Variables
  dialogVisible = signal(false);
  boundName = signal('');
  boundCode = signal('');
  savingBound = signal(false);
  isEditMode = signal(false);
  editingBoundId = signal<number | null>(null);

  deleteButtonLabel = computed(() => {
    return this.selectedBounds().length > 1 ? 'Delete Multiple' : 'Delete';
  });

  override ngOnInit(): void {
    super.ngOnInit();

    // Success Alert
    this.actions$.pipe(
      ofType(action.createBoundSuccess, action.updateBoundSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingBound.set(false);
      this.closeDialog();
      this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
    })

    // Error Alert
    this.actions$.pipe(
      ofType(action.deleteBoundSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.selectedBounds.set([]);
      this.loadBounds(); 
    })

    // Handle Error
    this.actions$.pipe(
      ofType(action.createBoundError, action.updateBoundError, action.deleteBoundError),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingBound.set(false);
    })
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
 }

  override loadData(page: number, size: number, filters: ColumnFilters): void {
    const keywords = filters['keywords'] || '';
    this.store$.dispatch(action.findBounds({
      page,
      size,
      keywords
    }));
  }

  loadBounds(): void {
    this.store$.dispatch(action.findBounds({ page: 1, size: this.rows, keywords: ''}))
  }

  addNew(): void {
    this.boundName.set('');
    this.boundCode.set('');
    this.isEditMode.set(false);
    this.dialogVisible.set(true);
    this.editingBoundId.set(null);
  }

  closeDialog(): void {
    this.boundName.set('');
    this.boundCode.set('');
    this.savingBound.set(false);
    this.isEditMode.set(false);
    this.dialogVisible.set(false);
    this.editingBoundId.set(null);
  }

  saveBound(): void {
    if(!this.boundName().trim() || !this.boundCode().trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Bound name and bound code are required'
      });
      return;
    }

    this.savingBound.set(true);

    const boundData: Bound = {
      id: this.editingBoundId() || 0,
      bound_name: this.boundName().trim(),
      bound_code: this.boundCode().trim()
    };

    if(this.isEditMode() && this.editingBoundId()) {
      this.store$.dispatch(action.updateBound({ id: this.editingBoundId()!, bound: boundData}))
    } else {
      this.store$.dispatch(action.createBound({ bound: boundData }))
    }
  }

  editBound(): void {
    if (this.selectedBounds().length === 1) {
      const boundToEdit = this.selectedBounds()[0];
      this.boundName.set(boundToEdit.bound_name ? boundToEdit.bound_name : '');
      this.boundCode.set(boundToEdit.bound_code ? boundToEdit.bound_code : '');
      this.isEditMode.set(true);
      this.editingBoundId.set(boundToEdit.id);
      this.dialogVisible.set(true);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select exactly one bound to edit'
      });
    }
  }

  deleteBound(): void {
    if (this.selectedBounds().length === 0) {
      return;
    }

    const boundsToDelete = [...this.selectedBounds()];
    const count = boundsToDelete.length;
    
    let message: string;
    if (count === 1) {
      message = `Are you sure you want to delete "${boundsToDelete[0].bound_name}"?`;
    } else {
      const maxDisplay = 10;
      const codesToShow = boundsToDelete.slice(0, maxDisplay);
      const codesList = codesToShow.map((b, index) => `${index + 1}. ${b.bound_code}`).join('<br>');
      
      let fullMessage = `Are you sure you want to delete ${count} bounds?<br><br><strong>Codes to be deleted:</strong><br>${codesList}`;
      
      if (count > maxDisplay) {
        fullMessage += '<br>etc.';
      }
      
      message = fullMessage;
    }

    this.confirmationService.confirm({
      message: message,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        boundsToDelete.forEach(bound => {
          this.store$.dispatch(action.deleteBound({ id: bound.id }));
        });
      }
    });
  }

  refreshBounds(): void {
    this.selectedBounds.set([]);
    this.clearFilter();
    this.messageService.add({
      severity: 'info',
      summary: 'Refreshed',
      detail: 'Bounds list has been refreshed'
    });
  }

  showHistory(): void {
    const selected = this.selectedBounds();
    if (selected.length !== 1) {
      this.messageService.add({
        severity: 'info',
        summary: 'History',
        detail: 'Select one record to view history'
      });
      return;
    }
    const item = selected[0] as any;
    this.historyRecordId = item?.id ?? null;
    this.historyRecordLabel = item?.bound_name || item?.name || String(this.historyRecordId ?? '');
    this.historyVisible = true;
  }
}
